import {
    amIImpersonating,
    EStorageType,
    getStorage,
    setStorage,
    TBrand,
    TColors,
    TemplateModel,
    UserModel
} from "@cakemail-org/ui-components-v2"
import { t } from "i18next"
import { MouseEvent } from "react"
import { affixToPercent, logError } from "../data"
import { TGetComparedStat, TGetCurrentStoredFilters, TValidateSystemEmailContentReturn } from "./types"
import { ESystemEmailType } from "../../container/systemEmails/components/tableListing/types"
import { TIconName } from "@cakemail-org/ui-components-v2/dist/esm/components/icon/types"
import { ELogErrorMsg } from "../data/types"
import { getLanguageUrlSegment } from "../../data/language"

export function getCurrentStoredFilters<A>({ getFullStorage, key, storageType, accountId }: TGetCurrentStoredFilters): Partial<A> {
    const storedFilters: A = getStorage({ key: key, type: storageType || EStorageType.session }) || {}
    if (getFullStorage)
        return storedFilters

    if (!accountId) {
        logError(ELogErrorMsg.AccountIdForStoredFilters, { key, storageType })
        return {}
    }
    //@ts-ignore
    return storedFilters?.[amIImpersonating(true)] || storedFilters?.[String(accountId)] || {}
}

export function setCurrentStoredFilters<A>({ key, accountId, data }: { key: string, accountId: number, data: A }) {
    const currentStoredFilters: any = getCurrentStoredFilters({ key, getFullStorage: true })

    const cAccountId = amIImpersonating(true) || accountId

    setStorage({
        key,
        data: {
            ...currentStoredFilters,
            [cAccountId]: data
        },
        type: EStorageType.session
    })
}


export function validateSystemEmailContent({ template, systemEmailType }: { template: TemplateModel, systemEmailType: ESystemEmailType }): TValidateSystemEmailContentReturn {
    const content: string = template.content.type === "bee" ? JSON.stringify(template.content.json) : String(template.content.html)
    let requiredMergeTags

    switch (systemEmailType) {
        case ESystemEmailType.accountConfirm:
            requiredMergeTags = [{
                value: "[CLIENT.CONFIRMATION]",
                msg: t("system-emails-req-tag-client-confirmation")
            }]
            break
        case ESystemEmailType.contactDOptIn:
            requiredMergeTags = [{
                value: "[SYSTEM_EMAIL_DOUBLE_OPT_IN_LINK]",
                msg: t("system-emails-req-tag-contact-confirmation")
            }]
            break
        case ESystemEmailType.passwordReset:
            requiredMergeTags = [{
                value: "[RESET_PASSWORD_LINK]",
                msg: t("system-emails-req-tag-password-reset")
            }]
            break
        case ESystemEmailType.senderConfirm:
            requiredMergeTags = [{
                value: "[SENDER.CONFIRMATION_LINK]",
                msg: t("system-emails-req-tag-sender-confirmation")
            }]
            break
        case ESystemEmailType.userConfirm:
            requiredMergeTags = [{
                value: "[USER.CONFIRMATION]",
                msg: t("system-emails-req-tag-user-confirmation")
            }]

            break
    }

    const missingTags: TValidateSystemEmailContentReturn = []

    requiredMergeTags.forEach((tag) => {
        if (!content.includes(tag.value)) {
            missingTags.push(tag)
        }
    })

    return missingTags.length > 0 ? missingTags : undefined
}


export function getComparedReportStat<T>({ metrics, oldMetrics, stat, isRate, reverse }: TGetComparedStat<T>): { icon?: TIconName, value: string, color: TColors } | undefined {
    if (metrics && oldMetrics) {
        let mainM: number = 0
        let secondM: number = 0

        mainM = Number(metrics[stat])
        secondM = Number(oldMetrics[stat])

        let total = mainM
        if (total !== 0) {
            total = isRate ? mainM - secondM : (mainM - secondM) / Math.max(mainM, secondM) * 100
        }

        let colorComparator = total === 0 ? null : (reverse ? total < 0 : total > 0)

        return {
            icon: total === 0 ? undefined : total > 0 ? "ArrowUp10" : "ArrowDown10",
            value: affixToPercent(total),
            color: colorComparator === null ? "boulder" : colorComparator ? "secondary" : "warning"
        }
    }

    return
}

export function updateIframe(iframe?: HTMLIFrameElement, content?: string | null) {
    if (!iframe) return

    const document = iframe.contentDocument
    if (document?.body && content) {
        document.open()
        document.close()
        document.body.innerHTML = content || ""
    }
}


// link handling is temporary and will be replaced by sources from brand
export function handleDocumentationClick(
    event: MouseEvent,
    user: UserModel | undefined,
    partnerBrand: TBrand
): void {
    const name = event.currentTarget.getAttribute("data-name")

    if (!name || (!name.startsWith("doc-"))) {
        return
    }

    const userLanguage = user?.language || "en_US"
    const languageUrlSegment = getLanguageUrlSegment(userLanguage, partnerBrand)

    event.preventDefault()

    switch (name) {
        case "doc-started":
            window.open(`https://cakemail.dev/${languageUrlSegment}/guides/getting-started-email-api`, "_blank")
            break
        case "doc-activity":
            window.open(`https://cakemail.dev/${languageUrlSegment}/api/email-api`, "_blank")
            break
        default:
            return

    }
} 